"use client";

import clsx from "clsx";
import DomPurify from "isomorphic-dompurify";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { FadeIn } from "components/ui/FadeIn";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import Atom from "../Atom";
import { AssetType, AtomProps, sizeConfig } from "../Atom/types";
interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

interface ClickableBlockProps {
  url: string;
  hoverColor?: string;
}

interface AtomLayoutProps {
  atoms?: (AtomProps & { clickableBlock?: ClickableBlockProps })[];
  groupTitle?: string;
  groupSubtitle?: string;
  miniTitle?: string;
  tags?: string[];
  isLeftAlign?: boolean;
  gridSize?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  iconWidth?: number;
  iconHeight?: number;
  mediaHeight?: number;
  textColor?: string;
  bgColorAtom?: string;
  wrapperClass?: string;
  bgColor?: string;
  className?: string;
  assetType?: AssetType;
  titleTag?: React.ElementType;
  miniTitleTag?: React.ElementType;
  iconPosition?: "left" | "center";
  buttonPadding?: string;
  buttonPosition?: "top" | "bottom";
  titleIcon?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  } | null;
}

const BalancedHTMLContent: React.FC<{ htmlContent: string }> = ({ htmlContent }) => {
  return <span dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(htmlContent) }} />;
};

const AtomLayout: React.FC<AtomLayoutProps> = ({
  atoms = [],
  groupTitle,
  groupSubtitle,
  isLeftAlign,
  bgColorAtom,
  bgColor,
  tags = [],
  iconWidth,
  button1,
  button2,
  wrapperClass,
  iconHeight,
  textColor,
  miniTitle,
  assetType,
  gridSize,
  className,
  titleTag = "h2",
  miniTitleTag = "h3",
  iconPosition = "left",
  buttonPadding,
  buttonPosition = "bottom",
  titleIcon,
  mediaHeight
}) => {
  const TitleTag = titleTag;
  const MiniTitleTag = miniTitleTag;

  const atomsLength = atoms.length;
  const tagsLength = tags.length;

  const gridSizeClassNameByItems = clsx({
    "grid-cols-1": atomsLength === 1,
    "grid-cols-1 sm:grid-cols-2": atomsLength === 2,
    "grid-cols-1 sm:grid-cols-3": atomsLength === 3,
    "grid-cols-1 sm:grid-cols-4": atomsLength === 4,
    "grid-cols-1 sm:grid-cols-5": atomsLength === 5,
    "grid-cols-2 sm:grid-cols-6": atomsLength === 6,
    "grid-cols-2 sm:grid-cols-7": atomsLength >= 7
  });
  const gridSizeClassName = gridSize ? `sm:grid-cols-${gridSize}` : gridSizeClassNameByItems;

  const textAlignClass = isLeftAlign ? "text-left" : "text-center";
  const config = sizeConfig[(atomsLength.toString() as "1" | "2" | "3" | "4") || "1"];

  const ButtonComponent = (button1 || button2) && (
    <FadeIn>
      <div className={buttonPadding || ""}>
        <DualButtonCTA button1={button1} button2={button2} />
      </div>
    </FadeIn>
  );

  const renderAtom = (atom: AtomProps & { clickableBlock?: ClickableBlockProps }, index: number) => {
    const AtomComponent = (
      <Atom
        key={index}
        iconWidth={iconWidth}
        iconHeight={iconHeight}
        textColor={textColor}
        bgColor={bgColorAtom}
        assetType={assetType}
        isLeftAlign={isLeftAlign}
        mediaHeight={mediaHeight}
        {...atom}
        {...config}
        className={clsx(textAlignClass, {
          "flex flex-col items-center": iconPosition === "center"
        })}
      />
    );

    if (atom.clickableBlock) {
      const clickableBlockClass = clsx(
        "block transition-all duration-300 ease-in-out",
        "hover:outline hover:outline-2 hover:outline-offset-2",
        atom.clickableBlock.hoverColor ? `hover:bg-${atom.clickableBlock.hoverColor}` : "hover:bg-gray-50"
      );

      return (
        <Link key={index} href={atom.clickableBlock.url} className={clickableBlockClass}>
          {AtomComponent}
        </Link>
      );
    }

    return AtomComponent;
  };

  return (
    <section id="atom-layout-69" style={{ backgroundColor: bgColor, width: "100%" }} className={className}>
      <MaxWidthWrapper className={wrapperClass}>
        <div className="flex flex-col items-center justify-center">
          <div className="items-center justify-center align-middle">
            <div className="tile-title-container">
              {titleIcon && (
                <Image
                  src={titleIcon.src}
                  alt={titleIcon.alt}
                  className="relative mb-4 inline"
                  width={titleIcon.width}
                  height={titleIcon.height}
                />
              )}
              {miniTitle && <MiniTitleTag className="subby text-center">{miniTitle}</MiniTitleTag>}
              {groupTitle && (
                <TitleTag className="tile-main-header font-heading [text-wrap:balance]">
                  <BalancedHTMLContent htmlContent={groupTitle} />
                </TitleTag>
              )}
              {groupSubtitle && <p className="tile-subtitle">{groupSubtitle}</p>}
              {tagsLength > 0 && (
                <div className="flex items-center justify-center gap-x-2 overflow-auto whitespace-nowrap pt-8">
                  {tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="border-accent-700 text-accent-700 rounded-full border bg-white px-5 py-[6px] text-md font-medium capitalize leading-none"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {buttonPosition === "top" && ButtonComponent}

            {atomsLength > 0 && (
              <div className={clsx("grid gap-8 align-middle", gridSizeClassName)}>
                {atoms.map((atom, index) => renderAtom(atom, index))}
              </div>
            )}

            {buttonPosition === "bottom" && ButtonComponent}
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default AtomLayout;
